import React, { createContext, useState, useEffect, useContext } from 'react';
import { createStorefrontApiClient } from "@shopify/storefront-api-client";
import { gql, useMutation, useQuery } from '@apollo/client'
import { navigate } from 'gatsby';
import { disableScroll, enableScroll } from '../utils/helpers';

const client = createStorefrontApiClient({
  storeDomain: process.env.GATSBY_SHOPIFY_STORE_URL,
  publicAccessToken: process.env.GATSBY_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
  apiVersion: '2025-04',
});

const getLocalStorage = (value) => {
  try {
    return JSON.parse(localStorage.getItem(value))
  } catch (e) {
    return '';
  }
}

const defaultValues = {
  loading: false,
  cartIsOpen: false,
  menuIsOpen: false,
  searchIsOpen: false,
  scrollDisabled: false,
  lightboxOpen: false,
  viewedProducts: [],
  searchTerm: '',
  conCurrentVariant: null,
  setConCurrentVariant: () => {},
  selectedCategory: [],
  setSelectedCategory: () => {},
  toggleWish: () => { },
  addVariantToCart: () => {},
  addVariantsToCart: () => {},
  removeLineItem: () => {},
  updateLineItem: () => {},
  client,
}

const CART_QUERY = `
  cart {
    id
    checkoutUrl
    lines(first: 10) {
      nodes {
        id
        merchandise {
          ... on ProductVariant {
            id
            title
            product {
              id
              handle
              title
            }
            image {
              url
              altText
            }
          }
        }
        cost {
          amountPerQuantity {
            amount
          }
          compareAtAmountPerQuantity {
            amount
          }
        }
        quantity
      }
    }
    cost {
      subtotalAmount {
        amount
      }
      totalAmount {
        amount
      }
      totalTaxAmount {
        amount
      }
    }
  }
  userErrors {
    field
    message
  }
  warnings {
    code
    message
    target
  }
`

export const StoreContext = createContext(defaultValues)

const isBrowser = typeof window !== `undefined`

export const StoreProvider = ({ children }) => {
  const [wishList, setWishList] = useState(getLocalStorage('wishlist') || [])
  const [loading, setLoading] = useState(false)
  const [didJustAddToCart, setDidJustAddToCart] = useState(false)
  const [cartIsOpen, setCartIsOpen] = useState(defaultValues.cartIsOpen);
  const [menuIsOpen, setMenuIsOpen] = useState(defaultValues.menuIsOpen);
  const [searchIsOpen, setSearchIsOpen] = useState(defaultValues.searchIsOpen);
  const [customerAccessToken, setCustomerAccessToken] = useState({ accessToken: null });
  const [customerData, setCustomerData] = useState(getLocalStorage('customerData'));
  const [lightboxOpen, setLightboxOpen] = useState(defaultValues.lightboxOpen);
  const [lightboxContent, setLightboxContent] = useState(null);
  const [lightboxClass, setLightboxClass] = useState(defaultValues.lightboxClass);
  const [currentMenuItem, setCurrentMenuItem] = useState(null);
  const [viewedProducts, setViewedProducts] = useState(defaultValues.viewedProducts);
  const [searchTerm, setSearchTerm] = useState(defaultValues.searchTerm);
  const [conCurrentVariant, setConCurrentVariant] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [cart, setCart] = useState({});

  const toggleCartIsOpen = () => {
    if(cartIsOpen) {
      setTimeout(() => setDidJustAddToCart(false), 200)
    }
    setCartIsOpen(prevState => !prevState);
  }

  const toggleMenuIsOpen = () => {
    setMenuIsOpen(prevState => !prevState);
  }

  const closeMenu = () => {
    setMenuIsOpen(false);
  }

  const openMenu = () => {
    setMenuIsOpen(true);
  }

  const openSearch = () => {
    setSearchIsOpen(true);
  }

  const closeSearch = () => {
    setSearchIsOpen(false);
  }

  useEffect(() => {
    if(menuIsOpen) {
      disableScroll()
    } else {
      enableScroll();
    }
  }, [menuIsOpen])

  const toggleWish = (product) => {
    let list = wishList;
    if (list.indexOf(product) === -1) {
      list.push(product)
      setWishList(list);
    } else {
      list = list.filter(item => item !== product)
      setWishList(list)
    }
    isBrowser && localStorage.setItem('wishlist', JSON.stringify(list))
  }

  const createCart = async (lines = []) => {
    const createCartMutation = `mutation createCart($cartInput: CartInput) {
      cartCreate(input: $cartInput) {
        ${CART_QUERY}
      }
    }`;

    const { data, errors } = await client.request(createCartMutation, {
      variables: {
        cartInput: {
          lines: lines
        },
      },
    });

    if(data) {
      saveCartState(data.cartCreate.cart)
    }

    if(errors) {
      console.error(errors)
    }

    return data;
  }

  const addCartLines = async (lines) => {
    const cartLinesAddMutation = `mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        ${CART_QUERY}
      }
    }`;

    const { data, errors } = await client.request(cartLinesAddMutation, {
      variables: {
        cartId: cart.id,
        lines: lines,
      },
    });

    saveCartState(data.cartLinesAdd.cart)

    if(errors) {
      console.error(errors)
    }

    return data;
  }

  const addVariantToCart = (variantId, quantity, openCart = true) => {
    setLoading(true)

    const lines = [
      {
        merchandiseId: variantId,
        quantity: parseInt(quantity, 10),
      },
    ]

    if(!cart.id) {
      createCart(lines).then(() => {
        setLoading(false);
        openCart && setCartIsOpen(true);
      })
    } else {
      addCartLines(lines).then(() => {
        setLoading(false);
        openCart && setCartIsOpen(true);
      })
    }
  }

  const addVariantsToCart = (variantIds, openCart = true) => {
    setLoading(true)

    const lines = variantIds.map(id => {
      return {
        merchandiseId: id,
        quantity: 1,
      }
    })

    if(!cart.id) {
      createCart(lines).then(() => {
        setLoading(false);
        openCart && setCartIsOpen(true);
      })
    } else {
      addCartLines(lines).then(() => {
        setLoading(false);
        openCart && setCartIsOpen(true);
      })
    }
  }

  const updateLineItem = async (lineID, quantity) => {
    if(!cart.id) return false;
    setLoading(true)

    const lines = [
      {
        id: lineID,
        quantity: parseInt(quantity) || 1,
      }
    ];

    const cartLinesAddMutation = `mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        ${CART_QUERY}
      }
    }`;

    const { data, errors } = await client.request(cartLinesAddMutation, {
      variables: {
        cartId: cart.id,
        lines: lines,
      },
    })

    saveCartState(data.cartLinesUpdate.cart)
    setLoading(false)

    if(errors) {
      console.error(errors)
    }

    return data;
  }

  const removeLineItems = async (lineIDs) => {
    if(!cart.id) return false;
    setLoading(true)

    const cartLinesAddMutation = `mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        ${CART_QUERY}
      }
    }`;

    const { data, errors } = await client.request(cartLinesAddMutation, {
      variables: {
        cartId: cart.id,
        lineIds: lineIDs,
      },
    })

    saveCartState(data.cartLinesRemove.cart)
    setLoading(false)

    if(errors) {
      console.error(errors)
    }

    return data;
  }

  const removeLineItem = (lineID) => {
    removeLineItems([lineID])
  }

  const saveCartState = (cart) => {
    setCart(cart);
    localStorage.setItem('shopify_cart', JSON.stringify(cart));
  }

  const getCartState = () => {
    let cartData = localStorage.getItem('shopify_cart');
    if(cartData) {
      setCart(JSON.parse(cartData))
      return JSON.parse(cartData);
    }
    return false;
  }

  useEffect(() => {
    if(!cart.id) {
      const savedCart = getCartState();
      if(savedCart) {
        setCart(savedCart);
      }
    }
  }, [cart.id])

  const addViewedProduct = (product) => {
    if(!viewedProducts.map(prod => prod.handle).includes(product.handle)) {
      setViewedProducts(prevState => {
        if(prevState.length >= 10) { prevState.pop() }
        return [product, ...prevState];
      });
      
      // Track in Klaviyo if function exists
      if (typeof window !== 'undefined' && window.trackViewedProduct) {
        window.trackViewedProduct(product);
      }
    }
  }

  useEffect(() => {
    if(viewedProducts.length > 0) {
      localStorage.setItem('viewedProducts', JSON.stringify(viewedProducts));
    }
  }, [viewedProducts])

  useEffect(() => {
    const storedViewedProducts = localStorage.getItem('viewedProducts');
    if(storedViewedProducts) {
      setViewedProducts(JSON.parse(storedViewedProducts));
    }
  }, [])

  const addSearchTerm = (term) => {
    setSearchTerm(term);
  }

  useEffect(() => {
    const getSearchTerm = localStorage.getItem('blog_terms');
    if(getSearchTerm) {
      setSearchTerm(searchTerm);
    }
  }, [searchTerm])

  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,
        addVariantToCart,
        cart,
        addVariantsToCart,
        removeLineItem,
        updateLineItem,
        loading,
        toggleWish,
        wishList,
        cartIsOpen,
        menuIsOpen,
        searchIsOpen,
        setSearchIsOpen,
        toggleCartIsOpen,
        toggleMenuIsOpen,
        closeMenu,
        openMenu,
        closeSearch,
        openSearch,
        didJustAddToCart,
        disableScroll,
        enableScroll,
        customerAccessToken,
        setCustomerAccessToken,
        customerData,
        setCustomerData,
        lightboxOpen,
        setLightboxOpen,
        lightboxContent,
        setLightboxContent,
        lightboxClass,
        setLightboxClass,
        currentMenuItem,
        setCurrentMenuItem,
        viewedProducts,
        addViewedProduct,
        searchTerm,
        setSearchTerm,
        addSearchTerm,
        conCurrentVariant,
        setConCurrentVariant,
        selectedCategory,
        setSelectedCategory,
      }}
    >
      {children}
    </StoreContext.Provider>
  )
}

const CUSTOMER_INFO = gql`
  query($customerAccessToken: String!) {
    customer(customerAccessToken: $customerAccessToken) {
      id
      email
      firstName
      lastName
    }
  }
`

const CUSTOMER_SIGNOUT = gql`
  mutation customerAccessTokenDelete($customerAccessToken: String!) {
    customerAccessTokenDelete(customerAccessToken: $customerAccessToken) {
      deletedAccessToken
      deletedCustomerAccessTokenId
      userErrors {
        field
        message
      }
    }
  }
`

export const useAccount = () => {
  const { customerAccessToken, setCustomerAccessToken, customerData, setCustomerData } = useContext(StoreContext);

  const { customerInfo } = useQuery(CUSTOMER_INFO, {
    skip: !customerAccessToken || !customerAccessToken.accessToken || customerData,
    variables: { customerAccessToken: customerAccessToken?.accessToken },
  })

  useEffect(() => {
    if(customerInfo?.data?.customer) {
      localStorage.setItem('customerData', JSON.stringify(customerInfo.data.customer))
      setCustomerData(customerInfo.data.customer)
    }
  }, [customerInfo, setCustomerData])

  const setToken = (value) => {
    isBrowser && localStorage.setItem('customerAccessToken', JSON.stringify(value));
    setCustomerAccessToken(value);
  }

  const [customerSignoutFunction] = useMutation(CUSTOMER_SIGNOUT, {
    variables: customerAccessToken ? { customerAccessToken:  customerAccessToken.accessToken} : null,
    onCompleted: (data) => {
      if (data && data.customerAccessTokenDelete.userErrors.length) {
        console.error('Logout error: ', data.customerAccessTokenDelete.userErrors)
        return false;
      }
      setToken({ customerAccessToken: '' })
      setCustomerData('')
      if(isBrowser) { localStorage.removeItem('customerData') }
      navigate('/account/login')
    },
  })

  const userIsAuthenticated = () => {
    if(typeof window === 'undefined') { return false }

    /**
     * Sometimes it takes a second for customerAccessToken to be set, which
     * causes weird problems when we're trying to check if a user is
     * authenticated on page load. So here we are using a fallback that
     * checks localStorage if the access token state var hasn't been set yet.
     */
    const accessToken = customerAccessToken?.accessToken ? customerAccessToken : getLocalStorage('customerAccessToken');

    if(
      accessToken &&
      accessToken.expiresAt &&
      accessToken.expiresAt > new Date().toISOString()
    ) {
      return true;
    } else {
      signout();
      return false;
    }
  }

  const signout = () => {
    if(customerAccessToken.accessToken && isBrowser) {
      customerSignoutFunction();
    }
  }

  useEffect(() => {
    async function getAccount() {
      if (customerAccessToken.accessToken) return;

      if (!isBrowser) {
        setCustomerAccessToken({ accessToken: null });
      }

      const token = getLocalStorage('customerAccessToken');
      if (token) {
        setCustomerAccessToken(token);
      }
    }

    getAccount();
  }, [customerAccessToken?.accessToken, setCustomerAccessToken]);

  return {
    setToken,
    customerData,
    setCustomerData,
    signout,
    userIsAuthenticated,
  }
}
